// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type AriaAttributes,
  type FC,
  type MouseEvent,
  type MouseEventHandler,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { Button, Popover, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useConst } from '@shared/ui-kit/hooks';
import { IconButton, OptionsSelector } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';

export interface DatePickerProps extends AriaAttributes {
  readonly startDate?: Date;
  readonly endDate?: Date;
  readonly onDateChange?: (startDate: Date, endDate: Date) => void;
  readonly onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  readonly disabled?: boolean;
}

export const DatePicker: FC<DatePickerProps> = props => {
  const { t } = useTranslation();
  const MIN_YEAR_VALUE = useConst(1970);

  const days = useCallback((month: number, year: number) => {
    const formatter = new Intl.DateTimeFormat('en', {
      day: '2-digit',
    });

    return Array.from(
      { length: dayjs().month(month).year(year).daysInMonth() },
      (_, index) => ({
        label: formatter.format(
          dayjs()
            .month(month)
            .date(index + 1)
            .toDate(),
        ),
        value: (index + 1).toString(),
      }),
    );
  }, []);

  const months = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en', {
      month: '2-digit',
    });

    return Array.from({ length: 12 }, (_, index) => ({
      label: formatter.format(dayjs().month(index).toDate()),
      value: index.toString(),
    }));
  }, []);

  const years = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en', {
      year: 'numeric',
    });

    const currentYear = dayjs().year();

    return Array.from(
      { length: Math.abs(MIN_YEAR_VALUE - currentYear) },
      (_, index) =>
        formatter.format(
          dayjs()
            .year(currentYear - index)
            .toDate(),
        ),
    );
  }, [MIN_YEAR_VALUE]);

  const id = useId();

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null,
  );

  const [{ endDate, startDate }, setDate] = useState(() => ({
    startDate: props.startDate ?? new Date(),
    endDate: props.endDate ?? new Date(),
  }));

  const open = useMemo(() => Boolean(anchorElement), [anchorElement]);

  const isStartDateAfterEndDate = useMemo(
    () => dayjs(startDate).isAfter(dayjs(endDate)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startDate],
  );

  const isEndDateAfterStartDate = useMemo(
    () => dayjs(endDate).isBefore(dayjs(startDate)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endDate],
  );

  const hasError = useMemo(
    () => [isEndDateAfterStartDate, isStartDateAfterEndDate].some(Boolean),
    [isEndDateAfterStartDate, isStartDateAfterEndDate],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorElement(null);
  }, []);

  const onSave = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      /* istanbul ignore if */
      if (hasError) return;

      props.onDateChange?.(startDate, endDate);

      props.onClick?.(event);

      handleClose();
    },
    [endDate, handleClose, hasError, props, startDate],
  );

  /* istanbul ignore next */
  const onChangeValueSelected = useCallback(
    (
      onChange: (value: Date) => void,
      date: Date,
      unit: 'date' | 'month' | 'year',
    ) => {
      return (value: string) => {
        onChange(dayjs(date).set(unit, +value).toDate());
      };
    },
    [],
  );

  /* istanbul ignore next */
  const onChangeDate = useCallback(
    (setValue: typeof setDate, key: 'startDate' | 'endDate') => {
      return (value: Date) => {
        setValue(prev => ({ ...prev, [key]: value }));
      };
    },
    [],
  );

  const Input: FC<{
    htmlFor: string;
    label: string;
    onChange: (value: Date) => void;
    value: Date;
    defaultValue: string;
    error?: boolean;
  }> = useCallback(
    ({ htmlFor, label, onChange, value, defaultValue, error }) => (
      <fieldset
        className="flex w-full flex-col gap-y-2"
        data-testid={`date-picker-fieldset-${htmlFor}`}
      >
        <label
          htmlFor={`${id}-${htmlFor}`}
          className="px-4 text-base leading-8 text-primary-medium"
        >
          {label}
        </label>
        <div className="flex gap-x-9 [&>input]:w-12" id={`${id}-${htmlFor}`}>
          <OptionsSelector
            defaultText="mm"
            optionsAvailable={months}
            onChangeValueSelected={onChangeValueSelected(onChange, value, 'month')}
            selectType="white"
            multiple={false}
            selectedAll={false}
            checkbox={false}
            defaultValue={defaultValue}
            optionSelected={`${value.getMonth()}`}
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'bottom',
            }}
            dataTestId="date-picker-month-selector"
            error={error}
          />
          <OptionsSelector
            defaultText="dd"
            optionsAvailable={days(value.getMonth(), value.getFullYear())}
            onChangeValueSelected={onChangeValueSelected(onChange, value, 'date')}
            selectType="white"
            multiple={false}
            selectedAll={false}
            checkbox={false}
            defaultValue={''}
            optionSelected={`${value.getDate()}`}
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'bottom',
            }}
            dataTestId="date-picker-day-selector"
            error={error}
          />
          <OptionsSelector
            defaultText="yy"
            optionsAvailable={years}
            onChangeValueSelected={onChangeValueSelected(onChange, value, 'year')}
            selectType="white"
            multiple={false}
            selectedAll={false}
            checkbox={false}
            defaultValue={''}
            optionSelected={`${value.getFullYear()}`}
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'bottom',
            }}
            dataTestId="date-picker-year-selector"
            error={error}
          />
        </div>
      </fieldset>
    ),
    [days, id, months, onChangeValueSelected, years],
  );

  return (
    <div className="flex items-center justify-center">
      <Tooltip title="Select date range">
        <IconButton
          aria-describedby={id}
          icon="DatePicker"
          variant="icon"
          color={props['aria-selected'] ? 'main' : 'dark'}
          onClick={handleClick}
          value={0}
          data-testid="date-picker-button"
          aria-selected={props['aria-selected']}
          aria-expanded={open}
          disabled={props.disabled}
        />
      </Tooltip>
      <Popover
        open={open}
        id={id}
        anchorEl={anchorElement}
        onClose={handleClose}
        classes={{
          paper:
            '!max-w-[320px] !w-full !max-h-[215px] flex flex-col p-4 !bg-page !rounded-lg gap-y-3 items-center justify-center',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        data-testid="date-picker-popover"
      >
        <Input
          htmlFor="start-date"
          label="Select start date"
          value={startDate}
          defaultValue=""
          onChange={onChangeDate(setDate, 'startDate')}
          error={isStartDateAfterEndDate}
        />
        <hr className="flex h-px w-full" />
        <Input
          htmlFor="end-date"
          label="Select end date"
          value={endDate}
          defaultValue=""
          onChange={onChangeDate(setDate, 'endDate')}
          error={isEndDateAfterStartDate}
        />
        <div className="self-end">
          <Button onClick={onSave} data-testid="date-picker-save-button">
            {t('common.save', { ns: 'common' })}
          </Button>
        </div>
      </Popover>
    </div>
  );
};
